import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import http from "../../../http-common";

export default function WaitlistJoinModal({ fepkId, storageKey, onClose, onSuccess }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);

  // View state: 'join' or 'request_link'
  const [view, setView] = useState("join");

  // Form states for joining
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // null until the user picks: public display is an explicit choice, never a default
  const [showNamePublicly, setShowNamePublicly] = useState(null);
  const [honeypot, setHoneypot] = useState(""); // website

  // Form states for requesting link
  const [requestEmail, setRequestEmail] = useState("");

  const [loading, setLoading] = useState(false);

  // Prefill logged in user info
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
      setRequestEmail(user.email || "");
    }
  }, [user]);

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim() || showNamePublicly === null) {
      toast.error(t("Required fields are missing"));
      return;
    }

    setLoading(true);

    const ref = localStorage.getItem(`waitlist_ref_${fepkId}`);

    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        showNamePublicly,
        website: honeypot, // Honeypot field
      };
      if (ref) {
        payload.ref = ref;
      }

      const response = await http.post(`/waitlist/${fepkId}/join`, payload);

      if (honeypot) {
        // If honeypot filled, silently fail/success
        toast.success(t("Joined waitlist successfully"));
        onClose();
        return;
      }

      const data = response.data;
      if (data.token) {
        // Logged-in/verified user joins successfully
        localStorage.setItem(storageKey, data.token);
        localStorage.removeItem(`waitlist_ref_${fepkId}`);
        toast.success(t("Joined waitlist successfully"));
        onSuccess(data);
      } else if (data.status === "duplicate" || (data.message && data.message.includes("already registered"))) {
        toast.info(t("Email already registered. A status link can be requested if needed."));
        setView("request_link");
        setRequestEmail(email.trim());
      } else {
        // Anonymous user needs email verification
        localStorage.removeItem(`waitlist_ref_${fepkId}`);
        toast.success(t("Check your email to verify your registration"));
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t("Failed to join waitlist. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLinkSubmit = async (e) => {
    e.preventDefault();
    if (!requestEmail.trim()) {
      toast.error(t("Email is required"));
      return;
    }

    setLoading(true);

    try {
      await http.post(`/waitlist/${fepkId}/request-link`, {
        email: requestEmail.trim(),
      });
      toast.success(t("Status link sent to your email"));
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || t("Failed to send status link."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-p-4">
      <div className="tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-[#280D41] tw-border tw-border-[#712CB0]/30 tw-p-6 tw-shadow-2xl">
        
        {/* Header */}
        <div className="tw-mb-5 tw-flex tw-items-center tw-justify-between">
          <h2 className="tw-m-0 tw-text-xl tw-font-semibold tw-text-white">
            {view === "join" ? t("Join Waitlist") : t("Request Access Link")}
          </h2>
          <button
            onClick={onClose}
            className="tw-text-white/50 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-lg tw-leading-none"
          >
            ✕
          </button>
        </div>

        {view === "join" ? (
          <form onSubmit={handleJoinSubmit} className="tw-space-y-4">
            
            {/* Honeypot field (hidden offscreen for screen readers / autofill but not display none) */}
            <div className="tw-absolute tw-left-[-9999px]" aria-hidden="true">
              <label htmlFor="website-hp">{t("Leave this field empty")}</label>
              <input
                id="website-hp"
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-text-white/60 tw-mb-1">
                {t("First Name")} *
              </label>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="tw-w-full tw-bg-[#1C0039] tw-border tw-border-[#712CB0]/30 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-sm tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7]"
                placeholder={t("Enter your first name")}
              />
            </div>

            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-text-white/60 tw-mb-1">
                {t("Last Name")} *
              </label>
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="tw-w-full tw-bg-[#1C0039] tw-border tw-border-[#712CB0]/30 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-sm tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7]"
                placeholder={t("Enter your last name")}
              />
            </div>

            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-text-white/60 tw-mb-1">
                {t("Email Address")} *
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="tw-w-full tw-bg-[#1C0039] tw-border tw-border-[#712CB0]/30 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-sm tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7]"
                placeholder={t("Enter your email address")}
              />
            </div>

            <fieldset className="tw-m-0 tw-border-none tw-p-0 tw-space-y-2">
              <legend className="tw-mb-1 tw-p-0 tw-text-sm tw-font-semibold tw-text-white">
                {t("Show your first name on this waitlist?")} *
              </legend>
              <p className="tw-m-0 tw-text-xs tw-text-white/60">
                {t("Only your first name appears in Recent joins on this film page. You can change this later.")}
              </p>
              {[
                { value: true, label: t("Yes, show my first name") },
                { value: false, label: t("No, keep me anonymous") },
              ].map((opt) => (
                <label
                  key={String(opt.value)}
                  className={`tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-bg-[#1C0039] tw-px-4 tw-py-2.5 tw-text-sm tw-text-white tw-cursor-pointer tw-select-none ${
                    showNamePublicly === opt.value ? "tw-border-[#FF43A7]" : "tw-border-[#712CB0]/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="showNamePublicly"
                    checked={showNamePublicly === opt.value}
                    onChange={() => setShowNamePublicly(opt.value)}
                    className="tw-h-4 tw-w-4 tw-text-[#FF43A7] focus:tw-ring-[#FF43A7]"
                  />
                  {opt.label}
                </label>
              ))}
              {showNamePublicly === null && (
                <p className="tw-m-0 tw-text-xs tw-text-[#F87171]">{t("Pick one to continue.")}</p>
              )}
            </fieldset>

            <button
              type="submit"
              disabled={loading || showNamePublicly === null}
              className="tw-w-full tw-rounded-xl tw-bg-[#FF43A7] tw-py-3 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#e03290] disabled:tw-opacity-50 tw-transition-colors tw-border-none tw-cursor-pointer tw-mt-2"
            >
              {loading ? t("Joining...") : t("Join Waitlist")}
            </button>

            <div className="tw-text-center tw-pt-2">
              <button
                type="button"
                onClick={() => setView("request_link")}
                className="tw-bg-transparent tw-border-none tw-text-xs tw-text-[#FF43A7] hover:tw-underline tw-cursor-pointer"
              >
                {t("Already on the waitlist? Request access link")}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRequestLinkSubmit} className="tw-space-y-4">
            <p className="tw-text-xs tw-text-white/60">
              {t("Enter your registered email and we will send you a secure link to access your status details.")}
            </p>
            
            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-text-white/60 tw-mb-1">
                {t("Email Address")}
              </label>
              <input
                type="email"
                required
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
                className="tw-w-full tw-bg-[#1C0039] tw-border tw-border-[#712CB0]/30 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-sm tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7]"
                placeholder={t("Enter your email address")}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="tw-w-full tw-rounded-xl tw-bg-[#FF43A7] tw-py-3 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#e03290] disabled:tw-opacity-50 tw-transition-colors tw-border-none tw-cursor-pointer"
            >
              {loading ? t("Sending...") : t("Send Link")}
            </button>

            <div className="tw-text-center tw-pt-2">
              <button
                type="button"
                onClick={() => setView("join")}
                className="tw-bg-transparent tw-border-none tw-text-xs tw-text-[#FF43A7] hover:tw-underline tw-cursor-pointer"
              >
                {t("Back to Join Form")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
